import { useRef, useEffect } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { compose } from '@reduxjs/toolkit';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import Button from 'core/buttons/Button';
import TextButton from 'core/buttons/TextButton';
import FormContainer from 'core/forms/FormContainer';
import FormSelect from 'hook-form-controls/FormSelect';
import FormTextField from 'hook-form-controls/FormTextField';
import FormSelectPartner from 'hook-form-controls/FormSelectPartner';
import ValidationErrorMessages from 'core/errors/ValidationErrorMessages';

import Auth from 'utils/Auth';
import app from 'app/config/app';
import YupPassword from 'utils/YupPassword';
import {
  createFundingSource,
  updateFundingSource,
} from 'app/features/funding-sources/fundingSourceSaga';
import FormSelectCountry from 'hook-form-controls/FormSelectCountry';

YupPassword(Yup);

const fundingSourceSchema = Yup.object().shape({
  country: Yup.string().required('Select a country'),
  payment_name: Yup.string().required('Payment Name is required').min(1).max(50),
  payment_value: Yup.string().required('Payment Value is required').min(1).max(20),
  description: Yup.string().required('Description is required').max(100),
});

function FundingSourceForm({ isAddMode, fundingSource, retries, isCreated }) {
  const submitButtonRef = useRef(null);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const methods = useForm({
    defaultValues: {
      ...(fundingSource ? fundingSource : {}),
    },
    resolver: yupResolver(fundingSourceSchema),
    mode: 'onChange',
  });

  const {
    formState: { errors },
    handleSubmit,
    reset,
    getValues,
  } = methods;

  const onSubmit = async (data) => {
    dispatch(
      isAddMode
        ? createFundingSource(data)
        : updateFundingSource({ ...data, id: fundingSource.id }),
    );
  };

  useEffect(() => {
    if (retries > 0) submitButtonRef.current.click();
  }, [retries]);

  useEffect(() => {
    if (isCreated) reset({ partner_id: getValues('partner_id') });
  }, [isCreated]);

  return (
    <FormContainer>
      {app.isDevelopmentMode() && <ValidationErrorMessages errors={errors} />}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container rowSpacing={3} columnSpacing={4}>
            <Grid item sm={12}>
              <FormSelectCountry id="select-country" name="country" />
            </Grid>
            <Grid item sm={12} md={6}>
              <FormTextField name="payment_name" label="Payment Name" />
            </Grid>
            <Grid item sm={12} md={6}>
              <FormTextField name="payment_value" label="Payment Value" />
            </Grid>
            <Grid item sm={12}>
              <FormTextField name="description" label="Description" multiline />
            </Grid>
            <Grid item sm={12} md={12}>
              <Box display="flex" justifyContent="space-between">
                <TextButton type="button" color="error" onClick={() => navigate(-1)}>
                  Cancel
                </TextButton>
                <Button
                  ref={submitButtonRef}
                  type="submit"
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                >
                  {isAddMode ? 'Create New Funding Source' : 'Update Funding Source'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </FormContainer>
  );
}

export default compose(connect(mapStateToProps))(FundingSourceForm);

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    retries: ownProps.isAddMode
      ? state.fundingSources.create.retries
      : state.fundingSources.update.retries,
    isCreated: state.fundingSources.create.isCreated,
  };
}

FundingSourceForm.propTypes = {
  isAddMode: PropTypes.bool.isRequired,
  fundingSource: PropTypes.object,
  retries: PropTypes.number,
  isCreated: PropTypes.bool,
};
